import { GetterTree } from 'vuex'
import { state } from './state'
import {
  Address,
  SalesOrg,
  ShipToAddress,
  ShipToMapping,
  ShipToSoldToMapping,
  SoldToMapping,
} from '@/interfaces'
import { generateSalesOrderId } from '@/helpers'

export const getters: GetterTree<typeof state, typeof state> = {
  getPartById: state => (id: string) => {
    return state.parts.data.find((part: any) => part.partId === id)
  },
  getPullRequestById: state => (id: string) => {
    return {
      ...state.pullRequests,
      data: state.pullRequests.data.find((pull: any) => generateSalesOrderId(pull) === id),
    }
  },
  getShipmentById: state => (id: string) => {
    return {
      ...state.shipments,
      data: state.shipments.data.find((shipment: any) => generateSalesOrderId(shipment) === id),
    }
  },
  getCustomerShipToAddresses: (state): Address[] => {
    if (!state.customer.shipToMappings) return []
    return state.customer.shipToMappings.map((addressMapping: any) => {
      return addressMapping.value
    })
  },
  getCustomerSoldToAddresses: (state): Address[] => {
    if (!state.customer.soldToMappings) return []
    return state.customer.soldToMappings.map((addressMapping: any) => {
      return addressMapping.value
    })
  },
  getCustomerAddress: (state, getters) => (type: string, id: string) => {
    if (!type || !id) return null
    return type == 'shipTo'
      ? (getters.getCustomerShipToAddresses as Address[]).find(address => address.id === id)
      : (getters.getCustomerSoldToAddresses as Address[]).find(address => address.id === id)
  },
  getCustomerMappings: (state, getters) => (
    pullType: string,
    shipToId: string | undefined,
  ): ShipToSoldToMapping[] | SoldToMapping[] => {
    const { customer } = state
    if (!customer || !customer.shipToMappings || !customer.soldToMappings) return []
    if (pullType === 'PhysicalHub') {
      // If it's a physical hub, we pick the shipTo address first, so we have to find the shipTo
      // address mappings and get the shipTo addresses from them. We also need to filter them
      // for physical hubs.
      const shipToMappings = (customer.shipToMappings as ShipToMapping[]).find(
        addrMap => !!shipToId && addrMap.value.id === shipToId && !!addrMap.value.isPhysicalHub,
      )
      return (shipToMappings && shipToMappings.mapped) || []
    } else {
      // For Customer Owned Invenetory, this step is first, so soldToMappings are used directly
      // but need to be filtered by customer owned inventories.
      return customer.soldToMappings.filter(addrMap => !!addrMap.value.hasCustomerOwnedInventories)
    }
  },
  getSalesOrgs: (state, getters) => (
    pullType: string,
    shipToId: string | undefined,
    soldToId: string | undefined,
  ): SalesOrg[] => {
    const mappings = getters.getCustomerMappings(pullType, shipToId)
    if (pullType === 'PhysicalHub') {
      return Object.values(
        (mappings as ShipToSoldToMapping[])
          .map(m => m.mapped)
          .reduce((acc, val) => [...acc, ...val], [])
          .reduce((acc, val) => ({ ...acc, [val.id || '']: val }), {} as SalesOrg), // remove duplicates
      )
    } else {
      // For Customer Owned Inventory, Sold To is first, so find the selected address and return the sales orgs
      const soldToMapping = (mappings as SoldToMapping[]).find(
        mapping => mapping.value.id === soldToId,
      )
      if (soldToMapping) {
        return soldToMapping.mapped.map(mapping => mapping.value)
      }
    }
    return []
  },
  getBuyers: (state, getters) => (soldToId: string) => {
    return getters.customer.buyers[soldToId] || []
  },
  getAvailableInventoryCount: state => {
    return state.parts.data.filter(({ availableQuantity: aq }) => aq && aq > 0).length
  },
  getHistoricalPulls: state => {
    return state.pullRequests.data.filter(
      ({ status }) => status && status.toLowerCase() !== 'pending',
    )
  },
  getPendingPulls: state => {
    return state.pullRequests.data.filter(
      ({ status }) => status && status.toLowerCase() === 'pending',
    )
  },
  getPullsPendingCount: (state, getters) => {
    //Count only unique salesOrderNumbers
    return new Set(
      getters.getPendingPulls.map((pull: any) => {
        return pull.salesOrderNumber
      }),
    ).size
  },
  getPullsHistoricalCount: (state, getters) => {
    return getters.getHistoricalPulls.length
  },
  recentShipments: state => {
    const dateOffset = new Date()
    dateOffset.setDate(dateOffset.getDate() - 7)
    return state.shipments.data.filter((shipment: any) => {
      const date = new Date(shipment.dateShipped)
      return dateOffset.getTime() <= date.getTime()
    })
  },
  recentShipmentsCount: (state, getters) => {
    //Count only unique trackingId
    return new Set(
      getters.recentShipments.map((shipment: any) => {
        return shipment.trackingId
      }),
    ).size
  },
  getCurrentSubtab: state => (id: string) => {
    const subTab = state.currentSubtabs.find((element: any) => element.parent === id)
    return subTab ? subTab.subtab : ''
  },
  customer: state => {
    return state.customer
  },
  accountList: state => {
    return state.accountList
  },
  notifications: state => {
    return state.notifications
  },
  currentBreakpoint: state => {
    return state.currentBreakpoint || 'xl'
  },
  isCSO: state => {
    return state.isCSO
  },
  selectedpart: state => {
    return state.selectedpart
  },
}
